import { CheckIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Center,
  Checkbox,
  CircularProgress,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  IconProps,
} from '@chakra-ui/react'
import { MoreVertical } from 'components/atoms/Icons'
import { icons } from 'constant'
import { FC, memo, useLayoutEffect, useRef, useState } from 'react'
import { FormikProvider } from 'formik'
import Select from 'react-select'
import { DataMoreVerticalType } from '../SearchPost/hook'
import { ArticleSummaryReport } from './ArticleSummaryReport'
import {
  CompareCompanyReportProps,
  OptionCompany,
  useCompareCompanyReport,
} from './hook'
import { PeekCarousel } from './PeekCarousel'
import { CompanyAnalysisContent } from './CompanyAnalysis'

// Wrapper to filter out isChecked/isIndeterminate props that Checkbox passes to custom icon
const CustomCheckIcon: FC<
  IconProps & { isChecked?: boolean; isIndeterminate?: boolean }
> = ({
  isChecked: _isChecked,
  isIndeterminate: _isIndeterminate,
  ...props
}) => <CheckIcon {...props} />

/**
 * CompareCompanyReport molecules
 * @constructor
 */
export const CompareCompanyReport: FC<CompareCompanyReportProps> = props => {
  const {
    formik,
    company,
    selectedCompanies,
    handleSelectCompany,
    handleRemoveCompany,
    clearCache,
    handleSetPagePdf,
    filterByIndustry,
    setFilterByIndustry,
    selectedNewsId,
    companyAnalysis,
    compareError,
    selectedDetailNewsId,
    handleSetSelectedDetailNews,
    handleRemoveCompanyDetailNews,
    handleScrollBottom,
    baseNewsId,
    pdfPage,
    handleCopy,
    news_id,
    isClicked,
    index,
    setIndex,
    fetchCompareCompanies,
    isOpenDrawer,
    isLoadingCompare,
    idCopy,
  } = useCompareCompanyReport(props)

  const [menuOpen, setMenuOpen] = useState(false)
  const [opens, setOpens] = useState<boolean[]>([])
  const {
    isOpen: isSumaryModalOpen,
    onOpen: onSumaryModalOpen,
    onClose: onSumaryModalClose,
  } = useDisclosure()
  const {
    isOpen: isDetailModalOpen,
    onOpen: onDetailModalOpen,
    onClose: onDetailModalClose,
  } = useDisclosure()
  const dataMoreVertical: DataMoreVerticalType[] = [
    {
      icon: icons.EXPAND_CONTENT,
      title: '全画面表示',
    },
    {
      icon: icons.COPY,
      title: 'コピーする',
    },
  ]

  const formatOptionLabel = (opt: OptionCompany) => {
    const isComparable = opt.has_comparable_report === true
    const isMidTermPlan = formik.values.report_type === 4
    const tooltipLabel = isMidTermPlan
      ? '同一種類の文書がありません'
      : '同一種類・同一年度の文書がありません'
    const displayName =
      isMidTermPlan && opt.report_year
        ? `${opt.company_name}（${opt.report_year}）`
        : opt.company_name

    return isComparable ? (
      <Flex
        key={opt.news_id}
        align="center"
        cursor={isComparable ? 'pointer' : 'not-allowed'}
        opacity={isComparable ? 1 : 0.5}
        onClick={() => isComparable && handleSelectCompany(opt)}
      >
        {!isDetailModalOpen ? (
          <Image
            pr={2}
            src={
              isComparable
                ? selectedCompanies.some(c => c.news_id === opt.news_id)
                  ? icons.CHECK_ON_MINUS
                  : icons.CHECK_ON_PLUS
                : icons.CHECK_ON_MINUS
            }
          ></Image>
        ) : null}

        <Text fontSize="14px">{displayName}</Text>
      </Flex>
    ) : (
      <Tooltip
        key={opt.company_id}
        backgroundColor="#FFFFFFE5"
        border="1px solod #E8ECEF"
        borderRadius="24px"
        color="black"
        fontSize="12px"
        label={
          <Flex align="center" h={6} justify="center" opacity={0.6}>
            {tooltipLabel}
          </Flex>
        }
        marginLeft="-150px"
        placement="right"
        px={4}
        textAlign="center"
      >
        <Flex
          key={opt.company_id}
          align="center"
          cursor={isComparable ? 'pointer' : 'not-allowed'}
          opacity={isComparable ? 1 : 0.5}
          onClick={() => isComparable && handleSelectCompany(opt)}
        >
          {!isDetailModalOpen ? (
            <Image
              pr={2}
              src={
                isComparable
                  ? selectedCompanies.some(c => c.company_id === opt.company_id)
                    ? icons.CHECK_ON_MINUS
                    : icons.CHECK_ON_PLUS
                  : icons.CHECK_ON_MINUS
              }
            ></Image>
          ) : null}

          <Text fontSize="14px">{opt.company_name}</Text>
        </Flex>
      </Tooltip>
    )
  }

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const scrollTopRef = useRef(0)
  const CustomMenuList = memo((props: any) => {
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      scrollTopRef.current = e.currentTarget.scrollTop
      const target = e.currentTarget
      const isBottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 5
      if (isBottom) handleScrollBottom()
    }

    useLayoutEffect(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollTopRef.current
      }
    }, [])

    return (
      <Box
        backgroundColor="#FEFEFE"
        border="1px solid #E8ECEF"
        borderRadius="12px"
        boxShadow="0 4px 12px rgba(0,0,0,0.15)"
        overflow="visible"
        pt={2}
        w="350px"
      >
        <Box
          ref={scrollRef}
          maxH="230px"
          overflowY="auto"
          sx={{
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E0',
              borderRadius: '3px',
            },
          }}
          onScroll={handleScroll}
        >
          {props.children}
        </Box>
        {!isDetailModalOpen ? (
          <Box mt={3} px={4}>
            <Text fontSize="14px">
              あと {5 - selectedCompanies.length} 社選択可能です
            </Text>
            <Button
              bg="transparent"
              border="1px solid #0084FF"
              borderRadius="12px"
              height="48px"
              my={3}
              w="full"
              onClick={() => setMenuOpen(false)}
            >
              <Text color="#0084FF">とじる</Text>
            </Button>
          </Box>
        ) : (
          []
        )}
      </Box>
    )
  })

  const sliderRef = useRef<HTMLDivElement>(null)
  const count = selectedDetailNewsId.length

  let itemWidth = '100%'
  if (count === 2) itemWidth = '49.5%'
  if (count === 3) itemWidth = '32.8%'
  if (count >= 4) itemWidth = '28%'

  let isDown = false
  let startX = 0
  let scrollLeft = 0

  const onMouseDown = (e: React.MouseEvent) => {
    isDown = true
    if (sliderRef.current) {
      startX = e.pageX - sliderRef.current.offsetLeft
      scrollLeft = sliderRef.current.scrollLeft
    }
  }

  const onMouseLeave = () => {
    isDown = false
    sliderRef.current!.classList.remove('active')
  }

  const onMouseUp = () => {
    isDown = false
    sliderRef.current!.classList.remove('active')
  }

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDown) return
    e.preventDefault()
    const x = e.pageX - sliderRef.current!.offsetLeft
    const walk = (x - startX) * 1.5
    sliderRef.current!.scrollLeft = scrollLeft - walk
  }

  return (
    <>
      <Box p={4}>
        <Box width="100%">
          <Flex
            flexWrap="wrap"
            gap={2}
            pb={3}
            sx={{
              '&::-webkit-scrollbar': {
                height: '8px',
                backgroundColor: '#f0f0f0',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
              },
            }}
          >
            {selectedCompanies.map((c, i1) => (
              <Flex
                key={`key_${i1}`}
                alignItems="center"
                bg="#FFF"
                border="1px solid #E8ECEF"
                borderRadius="12px"
                flex="0 0 auto"
                fontSize="14px"
                height="40px"
                px={3}
                py={2}
              >
                {c.company_code}: {c.company_name}
                {formik.values.report_type === 4 && c.report_year
                  ? ` (${c.report_year})`
                  : ''}
                {i1 !== 0 && (
                  <Image
                    cursor="pointer"
                    ml={2}
                    src={icons.CLOSE}
                    onClick={() =>
                      handleRemoveCompany(
                        Number(c.news_id || c.report_id || c.company_id),
                      )
                    }
                  ></Image>
                )}
              </Flex>
            ))}
          </Flex>
        </Box>
        <Box>
          <Text fontSize="14px" fontWeight="500">
            <b>{props.newsDetail.category}</b>
          </Text>

          <FormikProvider value={formik}>
            <Flex align="center" gap={8} pb={3}>
              <Flex alignItems="center" gap={2} h="64px">
                <Checkbox
                  icon={<CustomCheckIcon boxSize={5} />}
                  isChecked={filterByIndustry}
                  sx={{
                    '.chakra-checkbox__control': {
                      w: '30px',
                      h: '30px',
                      borderRadius: '6px',
                      border: '1px solid #007BFF',
                      bg: filterByIndustry ? '#007BFF' : 'white',
                      color: 'transparent',
                      boxShadow: 'none',
                      _checked: {
                        bg: '#007BFF',
                        color: 'white',
                        border: 'none',
                        _hover: {
                          bg: '#007BFF',
                        },
                        _active: {
                          bg: '#007BFF',
                        },
                      },
                    },
                  }}
                  onChange={() => {
                    setFilterByIndustry(prev => {
                      const nextValue = !prev
                      fetchCompareCompanies(
                        { is_same_industry: nextValue },
                        true,
                      )
                      return nextValue
                    })
                  }}
                ></Checkbox>
                <Text fontSize="14px" fontWeight="500">
                  同業種に絞る
                </Text>
              </Flex>

              <Select
                isClearable
                closeMenuOnSelect={false}
                components={{ MenuList: CustomMenuList }}
                filterOption={null}
                formatOptionLabel={formatOptionLabel}
                isOptionDisabled={option => !option.has_comparable_report}
                menuIsOpen={menuOpen}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuShouldScrollIntoView={false}
                noOptionsMessage={() => null}
                options={company}
                placeholder="比較する会社名を入力"
                styles={{
                  control: base => ({
                    ...base,
                    borderRadius: '12px',
                    borderColor: '#E8ECEF',
                    width: '250px',
                    height: '40px',
                    boxShadow: '0px 5px 10px -5px #1417181A inset',
                    backgroundColor: '#FEFEFE',
                    '&:hover': { borderColor: '#0084FF', cursor: 'pointer' },
                    paddingLeft: '8px',
                  }),
                  placeholder: base => ({
                    ...base,
                    opacity: 0.3,
                  }),
                  input: base => ({
                    ...base,
                    textAlign: 'left',
                  }),
                  valueContainer: base => ({
                    ...base,
                    padding: '0 !important',
                    margin: 0,
                  }),
                  menu: base => ({
                    ...base,
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    padding: '0',
                    position: 'absolute',
                  }),
                  option: (base, state) => ({
                    ...base,
                    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                    backgroundColor: state.isSelected ? '#FEFEFE' : '#FFF',
                    color: state.isDisabled ? '#999' : '#000',
                    '&:hover': {
                      backgroundColor: state.isDisabled ? '#FFF' : '#E6F2FF',
                    },
                  }),
                  indicatorsContainer: base => ({
                    ...base,
                    color: '#3182ce',
                    svg: { fill: '#3182ce' },
                  }),
                  dropdownIndicator: base => ({
                    ...base,
                    color: '#3182ce',
                    '&:hover': { color: '#2B6CB0' },
                  }),
                  indicatorSeparator: () => ({
                    display: 'none',
                  }),
                  clearIndicator: base => ({
                    ...base,
                    color: '#3182ce',
                    cursor: 'pointer',
                    '&:hover': { color: '#2B6CB0' },
                  }),
                }}
                value={null}
                onChange={option => {
                  if (option?.has_comparable_report) {
                    handleSelectCompany(option)
                  }
                }}
                onInputChange={(value, { action }: { action: string }) => {
                  if (action !== 'input-change') return value
                  setTimeout(() => {
                    fetchCompareCompanies(
                      {
                        company_name: value,
                        is_same_industry: filterByIndustry,
                      },
                      true,
                    )
                  }, 300)
                }}
                onMenuClose={() => setMenuOpen(false)}
                onMenuOpen={() => setMenuOpen(true)}
              />

              <Button
                _hover={{ bg: '#0084FF' }}
                bg="#0084FF"
                borderRadius="12px"
                color="white"
                h="40px"
                isDisabled={isClicked}
                ml="auto"
                px={6}
                py={3}
                w="112px"
                onClick={() => formik.handleSubmit()}
              >
                比較開始
              </Button>
            </Flex>
          </FormikProvider>

          <Box
            bg="gray.600"
            borderRadius="20px"
            boxSizing="border-box"
            gap={3}
            mb="24px"
            w="100%"
          >
            <Box p={3}>
              <Flex>
                <Text fontSize="16px" fontWeight="700">
                  <b>共通点・差異分析結果</b>
                </Text>
                <Menu>
                  <MenuButton mb="5px" ml="auto">
                    <MoreVertical
                      color="gray.800"
                      fontSize="2xl"
                      opacity={0.3}
                    />
                  </MenuButton>
                  <MenuList
                    bgColor="black.300"
                    borderRadius="20px"
                    display="flex"
                    flexDirection="column"
                    gap="16px"
                    maxH="194px"
                    maxW="337px"
                    overflow="auto"
                    overflowY={'auto'}
                    padding="24px"
                    zIndex={2}
                  >
                    {dataMoreVertical.map((item, index2) => (
                      <MenuItem
                        key={`key2_${index2}`}
                        bgColor="transparent"
                        gap="16px"
                        padding={0}
                        onClick={() => {
                          if (item.title === '全画面表示') {
                            onSumaryModalOpen()
                          } else {
                            handleCopy(news_id.toString())
                          }
                        }}
                      >
                        <Flex align="center" gap="16px">
                          <Image src={item.icon} />
                          <Text
                            color="white.50"
                            fontSize="sm"
                            fontWeight="medium"
                            letterSpacing="-0.02em"
                            lineHeight="shorter"
                          >
                            {item.title}
                          </Text>
                        </Flex>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </Flex>

              {companyAnalysis ? (
                <Box
                  fontSize="14px"
                  height={'18vh'}
                  id={news_id.toString()}
                  overflow={'auto'}
                  sx={{
                    '&::-webkit-scrollbar': {
                      width: '4px',
                      height: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#A0AEC0',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#718096',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#EDF2F7',
                      borderRadius: '4px',
                    },
                  }}
                >
                  <CompanyAnalysisContent
                    companyAnalysis={companyAnalysis}
                    report_type={formik.values.report_type}
                    selectedCompanies={selectedCompanies}
                  ></CompanyAnalysisContent>
                </Box>
              ) : isLoadingCompare ? (
                <Center my="50px">
                  <CircularProgress
                    isIndeterminate
                    color="blue.900"
                    size={10}
                  />
                </Center>
              ) : compareError ? (
                <Box height={'18vh'}>
                  <Text
                    color="#e5081b"
                    fontSize="sm"
                    fontWeight="bold"
                    textTransform="uppercase"
                  >
                    要約比較が生成できませんでした。しばらくたってから再度実行をお願いします。
                  </Text>
                </Box>
              ) : (
                <Box height={'18vh'} opacity="0.4">
                  <Text>
                    {selectedCompanies[0].company_name}{' '}
                    を基準に、選択した企業との比較結果を表示します。
                    <br />
                    ※ 比較対象の企業は最大4社まで選択可能です。 <br />※
                    比較できるドキュメントは、同一種類・同一年度のものに限られます。
                  </Text>
                </Box>
              )}
            </Box>
          </Box>

          <Box>
            <PeekCarousel
              baseNewsId={baseNewsId}
              clearCache={clearCache}
              dataMoreVertical={dataMoreVertical}
              handleCopy={handleCopy}
              handleSetPagePdf={handleSetPagePdf}
              index={index}
              isDetailModalOpen={isDetailModalOpen}
              isOpenDrawer={isOpenDrawer}
              pdfPage={pdfPage}
              report_type={formik.values.report_type}
              selectedCompanies={selectedCompanies}
              setIndex={setIndex}
              onDetailModalOpen={onDetailModalOpen}
            ></PeekCarousel>
          </Box>
        </Box>
      </Box>

      <Modal
        isCentered
        isOpen={isSumaryModalOpen}
        motionPreset="scale"
        onClose={onSumaryModalClose}
      >
        <ModalOverlay
          backdropFilter="blur(6px)"
          bg="rgba(0, 0, 0, 0.6)"
          overflow="visible"
        />
        <ModalContent
          bgColor="gray.600"
          borderRadius="20px"
          boxShadow="0 0 40px rgba(0,0,0,0.4)"
          maxW="80vw"
          my="5vh"
          overflow="visible"
          p={5}
          w="80vw"
        >
          <Button
            _hover={{ bg: 'transparent' }}
            bg="transparent"
            position="absolute"
            px={0}
            right="-15px"
            top="-20px"
            onClick={onSumaryModalClose}
          >
            <img alt="close" src={icons.CLOSE_MODAL} />
          </Button>
          <Box paddingBottom="16px" width="100%">
            <Flex
              flexWrap="wrap"
              gap={1}
              sx={{
                '&::-webkit-scrollbar': {
                  height: '8px',
                  backgroundColor: '#f0f0f0',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '4px',
                },
              }}
            >
              <Flex
                alignItems="center"
                bg="transparent"
                flex="0 0 auto"
                fontSize="18px"
                fontWeight="700"
                height="40px"
                pr={3}
                py={2}
              >
                共通点・差異分析結果
              </Flex>
              {selectedCompanies.map((c, i3) => (
                <Flex
                  key={`key3_${i3}`}
                  alignItems="center"
                  bg="#FFF"
                  border="1px solid #E8ECEF"
                  borderRadius="12px"
                  flex="0 0 auto"
                  fontSize="14px"
                  height="40px"
                  px={3}
                  py={2}
                >
                  {c.company_code}: {c.company_name}
                  {formik.values.report_type === 4 && c.report_year
                    ? ` (${c.report_year})`
                    : ''}
                </Flex>
              ))}
            </Flex>
          </Box>
          <ModalBody bg="gray.600" p={0}>
            <Box
              bg="gray.600"
              gap={3}
              height="730px"
              mb={3}
              overflow="auto"
              pr={4}
              sx={{
                whiteSpace: 'pre-line',
                '&::-webkit-scrollbar': {
                  width: '6px',
                  height: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#A0AEC0',
                  borderRadius: '6px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#718096',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#EDF2F7',
                  borderRadius: '6px',
                },
              }}
              w="100%"
            >
              {companyAnalysis ? (
                <Box fontSize="14px" id={news_id.toString()}>
                  <CompanyAnalysisContent
                    companyAnalysis={companyAnalysis}
                    selectedCompanies={selectedCompanies}
                  ></CompanyAnalysisContent>
                </Box>
              ) : compareError ? (
                <Box>
                  <Text>
                    要約が生成できませんでした。しばらくたってから再度実行をお願いします。
                  </Text>
                </Box>
              ) : null}
            </Box>
            <Flex bottom="20px" justify="flex-end" right="20px">
              <Button
                _hover={{ filter: 'none' }}
                bg="#0084FF1A"
                border="1px solid #0084FF"
                color="#0084FF"
                height="28px"
                px={3}
                py={0.5}
                width="113px"
                onClick={() => handleCopy(news_id.toString())}
              >
                <Image mr={1.5} src={icons.COPY} w="13px" />
                <Box as="span" fontSize="14px" fontWeight="400">
                  {idCopy === Number(news_id) ? 'コピー済み' : 'コピーする'}
                </Box>
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isCentered
        isOpen={isDetailModalOpen}
        motionPreset="scale"
        onClose={onDetailModalClose}
      >
        <ModalOverlay
          backdropFilter="blur(6px)"
          bg="rgba(0, 0, 0, 0.6)"
          overflow="visible"
        />
        <ModalContent
          backgroundColor="#FFFFFF"
          borderRadius="20px"
          boxShadow="0 0 40px rgba(0,0,0,0.4)"
          maxW="80vw"
          my="5vh"
          overflow="visible"
          p={5}
          w="80vw"
        >
          <Button
            _hover={{ bg: 'transparent' }}
            bg="transparent"
            position="absolute"
            px={0}
            right="-15px"
            top="-20px"
            onClick={onDetailModalClose}
          >
            <img alt="close" src={icons.CLOSE_MODAL} />
          </Button>
          <Box paddingBottom="16px" width="100%">
            <Flex
              flexWrap="wrap"
              gap={1}
              sx={{
                '&::-webkit-scrollbar': {
                  height: '8px',
                  backgroundColor: '#f0f0f0',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '4px',
                },
              }}
            >
              <Flex
                alignItems="center"
                bg="transparent"
                flex="0 0 auto"
                fontSize="18px"
                fontWeight="700"
                height="40px"
                pr={3}
                py={2}
              >
                要約比較
              </Flex>
              {selectedCompanies
                .filter(sc => !selectedDetailNewsId.includes(sc))
                .map((c, i4) => (
                  <Flex
                    key={`key4_${i4}`}
                    alignItems="center"
                    bg="#F3F5F7"
                    border="1px solid #E8ECEF"
                    borderRadius="12px"
                    cursor="pointer"
                    flex="0 0 auto"
                    fontSize="14px"
                    height="40px"
                    px={3}
                    py={2}
                    onClick={() => handleSetSelectedDetailNews(c)}
                  >
                    <Image mr={2} src={icons.ADD} /> {c.company_name}
                    {formik.values.report_type === 4 && c.report_year
                      ? ` (${c.report_year})`
                      : ''}
                  </Flex>
                ))}
            </Flex>
          </Box>
          <ModalBody borderRadius="12px" p={2}>
            <Flex
              ref={sliderRef}
              align="start"
              gap={3}
              overflowX="hidden"
              userSelect="none"
              onMouseDown={onMouseDown}
              onMouseLeave={onMouseLeave}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
            >
              {selectedDetailNewsId.map((c, i5) => (
                <Box
                  key={`key5_${i5}`}
                  bgColor="gray.600"
                  borderRadius="12px"
                  flex="1"
                  maxW={itemWidth}
                  minW={itemWidth}
                  p={3}
                  position="relative"
                >
                  {selectedDetailNewsId.length > 1 && (
                    <Image
                      _hover={{ cursor: 'pointer' }}
                      opacity={0.4}
                      position="absolute"
                      right="16px"
                      src={icons.CLOSE_DETAIL_MODAL}
                      top="25px"
                      zIndex={9999}
                      onClick={() => handleRemoveCompanyDetailNews(c)}
                    />
                  )}

                  <Box mb={5}>
                    <Select
                      closeMenuOnSelect={false}
                      components={{ MenuList: CustomMenuList }}
                      filterOption={(option, inputValue) =>
                        option.data.company_name
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      }
                      formatOptionLabel={formatOptionLabel}
                      isOptionDisabled={option => !option.has_comparable_report}
                      menuIsOpen={opens[i5]}
                      menuPortalTarget={document.body}
                      menuShouldScrollIntoView={false}
                      noOptionsMessage={() => null}
                      options={selectedCompanies}
                      styles={{
                        control: base => ({
                          ...base,
                          borderRadius: '12px',
                          borderColor: '#E8ECEF',
                          width: '250px',
                          height: '40px',
                          boxShadow: '0px 5px 10px -5px #1417181A inset',
                          backgroundColor: '#FEFEFE',
                          '&:hover': {
                            borderColor: '#0084FF',
                            cursor: 'pointer',
                          },
                          paddingLeft: '8px',
                        }),
                        placeholder: base => ({
                          ...base,
                          opacity: 0.3,
                        }),
                        input: base => ({
                          ...base,
                          textAlign: 'left',
                        }),
                        valueContainer: base => ({
                          ...base,
                          padding: '0 !important',
                          margin: 0,
                        }),
                        menu: base => ({
                          ...base,
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          padding: '0',
                          position: 'absolute',
                        }),
                        menuPortal: base => ({
                          ...base,
                          zIndex: 9999,
                          pointerEvents: 'auto',
                        }),
                        option: (base, state) => ({
                          ...base,
                          cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                          backgroundColor: state.isSelected
                            ? '#FEFEFE'
                            : '#FFF',
                          color: state.isDisabled ? '#999' : '#000',
                          '&:hover': {
                            backgroundColor: state.isDisabled
                              ? '#FFF'
                              : '#E6F2FF',
                          },
                        }),
                        indicatorsContainer: base => ({
                          ...base,
                          color: '#3182ce',
                          svg: { fill: '#3182ce' },
                        }),
                        dropdownIndicator: base => ({
                          ...base,
                          color: '#3182ce',
                          '&:hover': { color: '#2B6CB0' },
                        }),
                        indicatorSeparator: () => ({
                          display: 'none',
                        }),
                        clearIndicator: base => ({
                          ...base,
                          color: '#3182ce',
                          cursor: 'pointer',
                          '&:hover': { color: '#2B6CB0' },
                        }),
                      }}
                      value={selectedDetailNewsId[i5]}
                      onChange={option => {
                        if (option) {
                          handleSetSelectedDetailNews(option, i5)
                        }

                        setOpens(prevState => {
                          let newOpens = [...prevState]
                          newOpens[i5] = false
                          return newOpens
                        })
                      }}
                      onMenuClose={() =>
                        setOpens(prevState => {
                          let newOpens = [...prevState]
                          newOpens[i5] = false
                          return newOpens
                        })
                      }
                      onMenuOpen={() =>
                        setOpens(prevState => {
                          let newOpens = [...prevState]
                          newOpens[i5] = true
                          return newOpens
                        })
                      }
                    />
                  </Box>
                  <Box
                    id={`${selectedCompanies.filter(
                      sc => sc.news_id === c.news_id,
                    )?.[0].news_id}`}
                  >
                    <Text fontSize="16px" fontWeight="700">
                      <b>
                        {
                          selectedCompanies.filter(
                            sc => sc.news_id === c.news_id,
                          )?.[0].news_title
                        }
                      </b>
                    </Text>
                    <ArticleSummaryReport
                      key={c.news_id}
                      avatar=""
                      baseNewsId={baseNewsId}
                      clearCache={clearCache}
                      company={
                        selectedCompanies.filter(
                          sc => sc.news_id === c.news_id,
                        )?.[0]
                      }
                      handleSetPagePdf={handleSetPagePdf}
                      isDetailModalOpen={isDetailModalOpen}
                      newsId={c.news_id?.toString()}
                      pdfPage={pdfPage ?? ''}
                      question=""
                      selectedNewsId={selectedNewsId}
                      onChangeInput={() => console.log()}
                      onEnter={() => console.log()}
                      onSubmit={() => console.log()}
                    />
                  </Box>
                  <Flex bottom="12px" justify="flex-end" right="12px">
                    <Button
                      _hover={{ filter: 'none' }}
                      bg="transparent"
                      color="#0084FF"
                      height="28px"
                      mr={6}
                      px={0}
                      py={0.5}
                      onClick={() =>
                        window.open(
                          `${window.location.origin}/dashboard/post/${
                            c.news_id
                          }?list_news_ai_id=${selectedCompanies.filter(
                            sc => sc.news_id === c.news_id,
                          )?.[0]
                            .list_news_ai_id}&company_id=${selectedCompanies.filter(
                            sc => sc.news_id === c.news_id,
                          )?.[0].company_id}&tab=0`,
                          '_blank',
                        )
                      }
                    >
                      <Image mr={1.5} src={icons.PDF} w="13px" />
                      <Box as="span" fontSize="14px" fontWeight="400">
                        原文を表示
                      </Box>
                    </Button>
                    <Button
                      _hover={{ filter: 'none' }}
                      bg="#0084FF1A"
                      border="1px solid #0084FF"
                      color="#0084FF"
                      height="28px"
                      px={3}
                      py={0.5}
                      width="113px"
                      onClick={() => handleCopy(c.news_id?.toString() ?? '')}
                    >
                      <Image mr={1.5} src={icons.COPY} w="13px" />
                      <Box as="span" fontSize="14px" fontWeight="400">
                        {idCopy === c.news_id ? 'コピー済み' : 'コピーする'}
                      </Box>
                    </Button>
                  </Flex>
                </Box>
              ))}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
